/** Shared campaign workshops and procurement. All stock belongs to campaign state. */
import { buildingTypeById } from '../../data/buildings.ts';
import { unitType } from '../../data/units.ts';
import { emptyResources, INDUSTRIAL_RESOURCE_KEYS, RESOURCE_KEYS, RESOURCE_LABELS, UNCAPPED_RESOURCES,
  type ResourceBundle, type ResourceKey } from '../types.ts';
import { cityAt, cityStockCap, drawOrder, tierOf } from './holdings.ts';
import type { CampaignState, CityState, Regiment } from './types.ts';

/** Workshop ages require progressively larger city holdings as well as realm age. */
export const CAMPAIGN_FACTORY_TIER_BY_AGE = [0, 5, 6, 7, 8, 9] as const;
export const CAMPAIGN_WORK_SECONDS_PER_TIER = 60;
const RAW_RESERVE = 120;
const RAW_GOODS = new Set<ResourceKey>(['timber', 'stone', 'iron', 'grain']);
const INDUSTRIAL_GOODS = new Set<ResourceKey>(INDUSTRIAL_RESOURCE_KEYS);
export const CAMPAIGN_INDUSTRIAL_RESERVE = 30;
const OUTPUT_RESERVE_SHARE = 0.2;

// Dependency order is fixed. Basic planks/runestone/ingots keep the old holding
// refinery alongside these workshops, so ordinary city development remains viable.
const FACTORIES = [
  'charcoal_kiln', 'wellspring', 'arcane_sawmill', 'runeworks', 'smeltery',
  'steelworks', 'toolsmith', 'frame_workshop', 'fitting_foundry',
  'soul_furnace', 'grafting_house', 'void_kiln', 'rune_forge', 'distillery',
  'armament_workshop', 'runic_joinery', 'lens_workshop',
  'ashworks', 'great_forge', 'core_foundry', 'the_hollowing', 'mourning_forge', 'heart_assembly',
] as const;

/** A reserve available at this province: treasury plus warehouses reachable by owned roads. */
export function campaignMaterialStock(c: CampaignState, nation: number, province: number): ResourceBundle {
  const stock = emptyResources(), crown = c.nations[nation];
  if (!crown || c.provinces[province]?.owner !== nation) return stock;
  for (const key of RESOURCE_KEYS) stock[key] = crown.treasury[key] ?? 0;
  for (const yard of drawOrder(c, nation, province)) {
    if (yard.owner !== nation || c.provinces[yard.at]?.owner !== nation) continue;
    for (const key of RESOURCE_KEYS) stock[key] += yard.stock[key] ?? 0;
  }
  return stock;
}

/** Validate every line before withdrawing any goods. Deterministic nearest-yard order. */
export function spendCampaignMaterials(c: CampaignState, nation: number, province: number, bill: Partial<ResourceBundle>): boolean {
  const stock = campaignMaterialStock(c, nation, province), crown = c.nations[nation];
  if (!crown || c.provinces[province]?.owner !== nation) return false;
  for (const key of RESOURCE_KEYS) {
    const need = bill[key] ?? 0;
    if (!Number.isFinite(need) || need < 0 || need > stock[key] + 1e-8) return false;
  }
  const yards = drawOrder(c, nation, province).filter(yard => yard.owner === nation && c.provinces[yard.at]?.owner === nation);
  for (const key of RESOURCE_KEYS) {
    let owed = bill[key] ?? 0;
    for (const yard of yards) {
      const take = Math.min(owed, yard.stock[key]);
      yard.stock[key] -= take; owed -= take;
      if (owed <= 0) break;
    }
    if (owed > 0) crown.treasury[key] = Math.max(0, crown.treasury[key] - owed);
  }
  return true;
}

/** Only the additional finished-goods bill; existing gold/iron/manpower pricing stays separate. */
export function regimentIndustrialCost(regiments: readonly Regiment[]): Partial<ResourceBundle> {
  const bill: Partial<ResourceBundle> = {};
  for (const regiment of regiments) {
    const cost = unitType(regiment.typeIdx).cost;
    for (const key of INDUSTRIAL_RESOURCE_KEYS) {
      const amount = (cost[key] ?? 0) * regiment.models;
      if (amount > 0) bill[key] = (bill[key] ?? 0) + amount;
    }
  }
  return bill;
}

export interface CampaignFactoryReport {
  buildingId: string;
  name: string;
  age: number;
  minTier: number;
  available: boolean;
  reason: string;
  /** Proportion of a full month's recipe that the current stock and storage permit. */
  throttle: number;
  /** Hollow workshops also spend the crown's available people. */
  manpowerPerMonth: number;
  inputs: Array<{ key: ResourceKey; perMonth: number }>;
  outputs: Array<{ key: ResourceKey; perMonth: number }>;
}

function factoryReport(c: CampaignState, nation: number, province: number, buildingId: string,
  inputAllowance?: Partial<ResourceBundle>): CampaignFactoryReport {
  const def = buildingTypeById(buildingId), city = cityAt(c, province), tier = tierOf(c, province);
  const age = def.tier, minTier = CAMPAIGN_FACTORY_TIER_BY_AGE[age] ?? 10;
  const seconds = CAMPAIGN_WORK_SECONDS_PER_TIER * Math.max(0, tier - 4);
  const inputs = RESOURCE_KEYS.filter(key => (def.consumes[key] ?? 0) > 0)
    .map(key => ({ key, perMonth: (def.consumes[key] ?? 0) * seconds }));
  const outputs = RESOURCE_KEYS.filter(key => (def.produces[key] ?? 0) > 0)
    .map(key => ({ key, perMonth: (def.produces[key] ?? 0) * seconds }));
  const manpowerPerMonth = def.toll > 0 ? seconds / def.toll : 0;
  let reason = !city || city.owner !== nation || c.provinces[province]?.owner !== nation ? 'Needs an owned city warehouse.'
    : c.provinces[province].siegeBy >= 0 ? 'The city is besieged.'
    : c.age < age ? `Needs Age ${age}.`
    : tier < minTier ? `Needs holding tier ${minTier}.` : '';
  const available = reason === '';
  let throttle = available ? 1 : 0;
  if (available && city) {
    const stock = campaignMaterialStock(c, nation, province);
    for (const line of inputs) {
      const spare = Math.min(Math.max(0, stock[line.key] - (RAW_GOODS.has(line.key) ? RAW_RESERVE : 0)), inputAllowance?.[line.key] ?? Infinity);
      const fraction = spare / Math.max(1e-9, line.perMonth);
      if (fraction < throttle) { throttle = fraction; reason = `Needs ${RESOURCE_LABELS[line.key]}.`; }
    }
    if (manpowerPerMonth > 0) {
      const fraction = Math.max(0, c.nations[nation].manpower) / manpowerPerMonth;
      if (fraction < throttle) { throttle = fraction; reason = 'Needs available people for the Hollow toll.'; }
    }
    for (const line of outputs) {
      if (UNCAPPED_RESOURCES.includes(line.key)) continue;
      const net = line.perMonth - (inputs.find(v => v.key === line.key)?.perMonth ?? 0);
      if (net <= 0) continue;
      const fraction = Math.max(0, cityStockCap(c, province) - city.stock[line.key]) / net;
      if (fraction < throttle) { throttle = fraction; reason = `Storage full: ${RESOURCE_LABELS[line.key]}.`; }
    }
  }
  return { buildingId, name: def.name, age, minTier, available, reason, throttle, manpowerPerMonth, inputs, outputs };
}

/** Potential full-supply rates. Monthly sharing and depot reserves can reduce them further. */
export function campaignIndustryReport(c: CampaignState, nation: number, province: number): {
  stock: ResourceBundle; factories: CampaignFactoryReport[];
} {
  return { stock: campaignMaterialStock(c, nation, province), factories: FACTORIES.map(id => factoryReport(c, nation, province, id)) };
}

/** One bounded month of work, called by the existing holding economy tick. */
export function manufactureCampaignGoods(c: CampaignState): void {
  // Source first, then its consumers, across every connected yard. Inserting a
  // city into the saved array in a different order cannot change who gets supplied.
  const cities = [...c.cities].sort((a, b) => a.at - b.at);
  const work: Array<{ city: CityState; recipe: CampaignFactoryReport; capacity: number }> = [];
  const pools = new Map<number, { opening: ResourceBundle; reserve: Partial<ResourceBundle>; demand: Partial<ResourceBundle> }>();
  // Cache only for this month. Captures, road cuts and newly chartered cities are
  // reflected on the next call without saved or observable derived state.
  for (const city of cities) {
    if (city.owner < 0 || c.provinces[city.at]?.owner !== city.owner) continue;
    const yards = drawOrder(c, city.owner, city.at).filter(y => y.owner === city.owner);
    const first = yards[0]?.at ?? city.at;
    const shared = pools.get(first);
    if (shared) { pools.set(city.at, shared); continue; }
    const pool = { opening: campaignMaterialStock(c, city.owner, city.at), reserve: {}, demand: {} };
    pools.set(city.at, pool);
    for (const yard of yards) pools.set(yard.at, pool);
  }
  for (const id of FACTORIES) for (const city of cities) {
    if (city.owner < 0 || c.provinces[city.at]?.owner !== city.owner) continue;
    const recipe = factoryReport(c, city.owner, city.at, id);
    if (!recipe.available) continue;
    let capacity = 1;
    for (const line of recipe.outputs) {
      if (UNCAPPED_RESOURCES.includes(line.key)) continue;
      const net = line.perMonth - (recipe.inputs.find(v => v.key === line.key)?.perMonth ?? 0);
      if (net > 0) capacity = Math.min(capacity, Math.max(0, cityStockCap(c, city.at) - city.stock[line.key]) / net);
    }
    if (recipe.manpowerPerMonth > 0) capacity = Math.min(capacity, c.nations[city.owner].manpower / recipe.manpowerPerMonth);
    work.push({ city, recipe, capacity });
    const pool = pools.get(city.at)!;
    for (const line of recipe.inputs) pool.demand[line.key] = (pool.demand[line.key] ?? 0) + line.perMonth * capacity;
  }
  for (let index = 0; index < work.length; index++) {
    const job = work[index], { city } = job, pool = pools.get(city.at)!;
    if (job.capacity <= 0) continue;
    const stock = campaignMaterialStock(c, city.owner, city.at), allowance: Partial<ResourceBundle> = {};
    for (const line of job.recipe.inputs) {
      const key = line.key;
      let reserve = RAW_GOODS.has(key) ? RAW_RESERVE : 0;
      if (INDUSTRIAL_GOODS.has(key)) {
        // Protect the opening depot plus a share of fresh output. A reserve
        // builds gradually; it never holds up the first downstream workshop.
        reserve = Math.max(pool.reserve[key] ?? 0, Math.min(CAMPAIGN_INDUSTRIAL_RESERVE,
          pool.opening[key] + Math.max(0, stock[key] - pool.opening[key]) * OUTPUT_RESERVE_SHARE));
        pool.reserve[key] = reserve;
      }
      const demand = pool.demand[key] ?? 0;
      // Every remaining consumer receives its share of a scarce input. Without
      // this, steelworks could consume every piece of charcoal before fittings
      // ever got a turn, and essence consumers could starve the whole upper Age.
      allowance[key] = Math.max(0, stock[key] - reserve) * line.perMonth * job.capacity / Math.max(1e-9, demand);
    }
    for (const line of job.recipe.inputs) pool.demand[line.key] = Math.max(0, (pool.demand[line.key] ?? 0) - line.perMonth * job.capacity);
    const recipe = factoryReport(c, city.owner, city.at, job.recipe.buildingId, allowance);
    if (recipe.throttle <= 0) continue;
    const bill: Partial<ResourceBundle> = {};
    for (const line of recipe.inputs) bill[line.key] = line.perMonth * recipe.throttle;
    if (!spendCampaignMaterials(c, city.owner, city.at, bill)) continue;
    c.nations[city.owner].manpower = Math.max(0, c.nations[city.owner].manpower - recipe.manpowerPerMonth * recipe.throttle);
    for (const line of recipe.outputs) {
      const amount = line.perMonth * recipe.throttle;
      if (UNCAPPED_RESOURCES.includes(line.key)) c.nations[city.owner].treasury[line.key] += amount;
      else city.stock[line.key] = Math.min(cityStockCap(c, city.at), city.stock[line.key] + amount);
    }
  }
}
